import { Button } from "antd";
import Title from "antd/es/typography/Title";
import queryClient from "api/queryClient";
import {
    useGetExternalFinalPageSettings,
    useStoreExternalFinalPageSettingMutation,
    useUpdateExternalFinalPageSettingMutation,
} from "api/TenantSlug/Offer/ExternalFinalPageSettings/hooks";
import { useGetOfferQuery, useUpdateOfferMutation } from "api/TenantSlug/Offer/hooks";
import {
    useGetProcessRefuseLeadSettings,
    useStoreProcessRefuseLeadSettingMutation,
    useUpdateProcessRefuseLeadSettingMutation,
} from "api/TenantSlug/Offer/ProcessRefuseLeadSettings/hooks";
import {
    useGetRefuseLeadSettings,
    useStoreRefuseLeadSettingMutation,
    useUpdateRefuseLeadSettingMutation,
} from "api/TenantSlug/Offer/RefuseLeadSettings/hooks";
import { OfferApiFormEditFormData, schema } from "components/Forms/OfferApiFormEditForm/formSchema";
import { ApiFormDesignSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormDesignSection.tsx";
import { ApiFormExternalFinalPageSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormExternalFinalPageSection.tsx";
import { ApiFormPrefillSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormPrefillSection.tsx";
import { ApiFormProcessRefusedLeadsSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormProcessRefusedLeadsSection.tsx";
import { ApiFormRefuseLeadsSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormRefuseLeadsSection.tsx";
import { ApiFormStructureSection } from "components/Forms/OfferApiFormEditForm/sections/ApiFormStructureSection.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { FC } from "react";
import { toast } from "react-toastify";
import { ConversionStateInput, RefuseLeadsType, RoleEnum } from "types/enums";
import { FormId } from "types/models";

interface OfferApiFormEditFormContentProps {
    conversionStateInput: ConversionStateInput;
    offerId: string;
}

const OfferApiFormEditFormContent: FC<OfferApiFormEditFormContentProps> = ({ conversionStateInput, offerId }) => {
    const { t: tC } = usePaldockTranslation("common");
    const { t } = usePaldockTranslation("forms.offer-api-form-edit");
    const { t: tOffers } = usePaldockTranslation("pages.offer.edit");
    const userUtils = useUser();

    const isIframeMode = conversionStateInput === ConversionStateInput.Iframe;

    const showDetailButton = userUtils
        ?.getActiveTenant()
        ?.userRoles?.some((role) => [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="flex flex-col gap-6">
                {/* Settings */}
                <SectionContentWrapper
                    withBackground
                    className={`${isIframeMode ? "h-[171px]" : "h-[269px]"} overflow-y-auto flex flex-col`}
                >
                    <div className="flex justify-between items-center mb-6">
                        <Title level={3} className="!mb-0">
                            {tC("settings")}
                        </Title>
                        {showDetailButton && (
                            <TenantLink
                                to={isIframeMode ? "/offer/detail/$offerId/form" : "/offer/detail/$offerId/api"}
                                params={{ offerId }}
                            >
                                <Button>{isIframeMode ? tOffers("get_form") : tOffers("get_api")}</Button>
                            </TenantLink>
                        )}
                    </div>
                    {isIframeMode ? <ApiFormDesignSection /> : <ApiFormStructureSection />}
                </SectionContentWrapper>

                {/* Prefill (Iframe only - second position) */}
                {isIframeMode && (
                    <SectionContentWrapper withBackground className="h-[171px] overflow-y-auto flex flex-col">
                        <Title level={3} className="!mb-6">
                            {t("sections.prefill.title")}
                        </Title>
                        <ApiFormPrefillSection />
                    </SectionContentWrapper>
                )}

                {/* External Final Page */}
                <SectionContentWrapper withBackground className="h-[318px] overflow-y-auto flex flex-col">
                    <Title level={3} className="!mb-6">
                        {t("sections.external_final_page.title")}
                    </Title>
                    <ApiFormExternalFinalPageSection />
                </SectionContentWrapper>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-6">
                {/* Refuse Leads */}
                <SectionContentWrapper
                    withBackground
                    className={`${isIframeMode ? "h-[342px]" : "h-[294px]"} overflow-y-auto flex flex-col`}
                >
                    <Title level={3} className="!mb-6">
                        {t("sections.refuse_leads.title")}
                    </Title>
                    <ApiFormRefuseLeadsSection />
                </SectionContentWrapper>

                {/* Process Refuse Leads */}
                <SectionContentWrapper
                    withBackground
                    className={`${isIframeMode ? "h-[342px]" : "h-[294px]"} overflow-y-auto flex flex-col`}
                >
                    <Title level={3} className="!mb-6">
                        {t("sections.process_refused_leads.title")}
                    </Title>
                    <ApiFormProcessRefusedLeadsSection />
                </SectionContentWrapper>
            </div>
        </div>
    );
};

interface OfferApiFormEditFormProps {
    offerId: string;
    conversionStateInput: ConversionStateInput;
}

export const OfferApiFormEditForm: FC<OfferApiFormEditFormProps> = ({ offerId, conversionStateInput }) => {
    const { t } = usePaldockTranslation("forms.offer-api-form-edit");

    const {
        data: offerData,
        isLoading: isOfferLoading,
        isError: isOfferError,
    } = useGetOfferQuery(offerId, {
        filter: {
            include: "forms,formStructures",
        },
    });

    const offer = offerData?.offer;

    const updateOfferMutation = useUpdateOfferMutation(offerId);

    const {
        data: refuseLeadSettings,
        isLoading: isRefuseLeadSettingsLoading,
        isError: isRefuseLeadSettingsError,
    } = useGetRefuseLeadSettings(offerId, {
        filter: {
            include: "includedUsers,excludedUsers,offer",
            filter: {
                order: 0,
                conversion_type: conversionStateInput,
            },
        },
    });

    const setting = refuseLeadSettings?.refuseLeadSettings?.[0];

    const storeRefuseLeadsMutation = useStoreRefuseLeadSettingMutation(offerId);
    const updateRefuseLeadsMutation = useUpdateRefuseLeadSettingMutation(offerId, setting?.id ?? "");

    const {
        data: processRefuseLeadSettings,
        isLoading: isProcessRefuseLeadSettingsLoading,
        isError: isProcessRefuseLeadSettingsError,
    } = useGetProcessRefuseLeadSettings(offerId, {
        filter: {
            include: "includedUsers,excludedUsers,offer",
            filter: {
                order: 0,
                conversion_type: conversionStateInput,
            },
        },
    });

    const processSetting = processRefuseLeadSettings?.refuseLeadSettings?.[0];

    const storeProcessRefuseLeadsMutation = useStoreProcessRefuseLeadSettingMutation(offerId);
    const updateProcessRefuseLeadsMutation = useUpdateProcessRefuseLeadSettingMutation(
        offerId,
        processSetting?.id ?? ""
    );

    const {
        data: externalFinalPageSettings,
        isLoading: isExternalFinalPageSettingsLoading,
        isError: isExternalFinalPageSettingsError,
    } = useGetExternalFinalPageSettings(offerId, {
        filter: {
            include: "includedUsers,excludedUsers,offer",
            filter: {
                order: 0,
                conversion_type: conversionStateInput,
            },
        },
    });

    const externalFinalPageSetting = externalFinalPageSettings?.externalFinalPageSettings?.[0];

    const isLoading =
        isOfferLoading ||
        isRefuseLeadSettingsLoading ||
        isProcessRefuseLeadSettingsLoading ||
        isExternalFinalPageSettingsLoading;

    const isError =
        isOfferError ||
        isRefuseLeadSettingsError ||
        isProcessRefuseLeadSettingsError ||
        isExternalFinalPageSettingsError;

    const storeExternalFinalPageMutation = useStoreExternalFinalPageSettingMutation(offerId);
    const updateExternalFinalPageMutation = useUpdateExternalFinalPageSettingMutation(
        offerId,
        externalFinalPageSetting?.id ?? ""
    );

    const onSubmit = async (data: OfferApiFormEditFormData) => {
        const offerPayload = {
            id: offerId,
            ...(conversionStateInput === ConversionStateInput.API && { form_structure_ids: data.form_structures }),
            ...(conversionStateInput === ConversionStateInput.Iframe && { form_ids: data.form_designs }),
            ...(conversionStateInput === ConversionStateInput.Iframe &&
                data.iframe_affiliate_id_rewrite !== undefined && {
                    iframe_affiliate_id_rewrite: data.iframe_affiliate_id_rewrite,
                }),
            ...(conversionStateInput === ConversionStateInput.Iframe &&
                data.iframe_auto_submit !== undefined && { iframe_auto_submit: data.iframe_auto_submit }),
            ...(conversionStateInput === ConversionStateInput.Iframe &&
                data.iframe_form_prefill !== undefined && { iframe_form_prefill: data.iframe_form_prefill }),
        };

        // Only update/create refuse leads if both required fields are provided
        const shouldUpdateRefuseLeads =
            data.refuse_leads_active !== undefined &&
            data.refuse_leads_active !== null &&
            data.refuse_leads_types !== undefined;

        const mutations: Array<Promise<unknown>> = [updateOfferMutation.mutateAsync(offerPayload)];

        if (shouldUpdateRefuseLeads) {
            const refuseLeadsPayload = {
                active: data.refuse_leads_active!,
                refuse_types: data.refuse_leads_types! as unknown as Array<App.Enums.RefuseLeadsType>,
                conversion_type: conversionStateInput,
                order: 0,
                included_user_ids: data.included_user_ids || [],
                excluded_user_ids: data.excluded_user_ids || [],
            };

            mutations.push(
                setting?.id
                    ? updateRefuseLeadsMutation.mutateAsync(refuseLeadsPayload)
                    : storeRefuseLeadsMutation.mutateAsync(refuseLeadsPayload)
            );
        }

        // Only update/create process refuse leads if both required fields are provided
        const shouldUpdateProcessRefuseLeads =
            data.process_refuse_leads_active !== undefined &&
            data.process_refuse_leads_active !== null &&
            data.process_refuse_leads_types !== undefined;

        if (shouldUpdateProcessRefuseLeads) {
            const processRefuseLeadsPayload = {
                active: data.process_refuse_leads_active!,
                refuse_types: data.process_refuse_leads_types! as unknown as Array<App.Enums.RefuseLeadsType>,
                conversion_type: conversionStateInput,
                order: 0,
                included_user_ids: data.process_included_user_ids || [],
                excluded_user_ids: data.process_excluded_user_ids || [],
            };

            mutations.push(
                processSetting?.id
                    ? updateProcessRefuseLeadsMutation.mutateAsync(processRefuseLeadsPayload)
                    : storeProcessRefuseLeadsMutation.mutateAsync(processRefuseLeadsPayload)
            );
        }

        // Only update/create external final page if active field is provided
        const shouldUpdateExternalFinalPage =
            data.external_final_page_active !== undefined && data.external_final_page_active !== null;

        if (shouldUpdateExternalFinalPage) {
            const externalFinalPagePayload = {
                active: data.external_final_page_active!,
                default_url: data.external_final_page_default_url || null,
                conversion_type: conversionStateInput,
                order: 0,
                included_user_ids: data.external_final_page_included_user_ids || [],
                excluded_default_final_page_user_ids:
                    data.external_final_page_excluded_default_final_page_user_ids || [],
                skip_internal_loader: data.external_final_page_skip_internal_loader ?? false,
                include_results_in_redirect: data.external_final_page_include_results_in_redirect ?? true,
            };

            mutations.push(
                externalFinalPageSetting?.id
                    ? updateExternalFinalPageMutation.mutateAsync(externalFinalPagePayload)
                    : storeExternalFinalPageMutation.mutateAsync(externalFinalPagePayload)
            );
        }

        const results = await Promise.allSettled(mutations);

        queryClient.invalidateQueries({ queryKey: ["getOffer", offerId] });
        queryClient.invalidateQueries({ queryKey: ["getRefuseLeadSettings", offerId] });
        queryClient.invalidateQueries({ queryKey: ["getProcessRefuseLeadSettings", offerId] });
        queryClient.invalidateQueries({ queryKey: ["getExternalFinalPageSettings", offerId] });

        if (results.some((result) => result.status === "rejected")) {
            toast.error(t("messages.update_error"));
            return;
        }

        toast.success(t("messages.update_success"));
    };

    if (isLoading || isError) {
        return <QueryState isLoading={isLoading} isError={isError} errorKey="errors.loading_offer" />;
    }

    if (!offer) {
        return <QueryState isLoading={false} isError={true} errorKey="errors.loading_offer" />;
    }

    return (
        <GeneralFormProvider<OfferApiFormEditFormData>
            key={offer.id}
            id={FormId.OfferApiFormEditForm}
            schema={schema}
            defaultValues={{
                form_designs: offer.forms?.map((item) => item.id?.toString()) || [],
                form_structures: offer.form_structures?.map((item) => item.id?.toString()) || [],
                refuse_leads_active: setting?.active,
                refuse_leads_types: setting?.refuse_types as RefuseLeadsType[] | undefined,
                included_user_ids: setting?.included_users?.map((user) => user.id?.toString()) || [],
                excluded_user_ids: setting?.excluded_users?.map((user) => user.id?.toString()) || [],
                process_refuse_leads_active: processSetting?.active,
                process_refuse_leads_types: processSetting?.refuse_types as RefuseLeadsType[] | undefined,
                process_included_user_ids: processSetting?.included_users?.map((user) => user.id?.toString()) || [],
                process_excluded_user_ids: processSetting?.excluded_users?.map((user) => user.id?.toString()) || [],
                iframe_form_prefill: offer.iframe_form_prefill,
                iframe_affiliate_id_rewrite: offer.iframe_affiliate_id_rewrite,
                iframe_auto_submit: offer.iframe_auto_submit,
                external_final_page_active: externalFinalPageSetting?.active,
                external_final_page_default_url: externalFinalPageSetting?.default_url ?? undefined,
                external_final_page_included_user_ids:
                    externalFinalPageSetting?.included_users?.map((user) => user.id?.toString()) ?? [],
                external_final_page_excluded_default_final_page_user_ids:
                    externalFinalPageSetting?.excluded_default_final_page_users?.map((user) => user.id?.toString()) ??
                    [],
                external_final_page_skip_internal_loader: externalFinalPageSetting?.skip_internal_loader ?? false,
                external_final_page_include_results_in_redirect:
                    externalFinalPageSetting?.include_results_in_redirect ?? true,
            }}
            onSubmit={onSubmit}
            resetOnSubmit={false}
        >
            <OfferApiFormEditFormContent conversionStateInput={conversionStateInput} offerId={offerId} />
        </GeneralFormProvider>
    );
};
